import React, {useMemo, useState} from "react";
import {Autocomplete} from "@mui/material";
import {Box, Chip} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {Button, TextField} from "../../../ui/components";
import {autocompleteChevronSx} from "../../../ui/components/inputStyles";
import {useGetTemplates} from "../../../apis/requests/templates/getTemplates";
import {ReachTemplatesFilters} from "../../../apis/requests/templates/types";
import {ReachPromoTemplateRef} from "../../../apis/requests/promos/types";

export type PromoTemplateOption = Omit<ReachPromoTemplateRef, "days_since_start">;
import {useDebounce} from "../../../hooks/useDebounce";
import {CHANNEL_DISPLAY} from "../../templates/templateDisplay";
import {INPUT_SX} from "./FormCard";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 50;

const searchFilters = (search: string): ReachTemplatesFilters => ({
  segmentIds: [],
  segmentMatchMode: "any",
  includeUnassignedSegments: false,
  contentChannels: [],
  statuses: [],
  types: [],
  search,
});

const templateLabel = (template: PromoTemplateOption) =>
  `${template.name} · ${CHANNEL_DISPLAY[template.content_channel].label}`;

type Props = {
  value: number[];
  onChange: (templates: PromoTemplateOption[]) => void;
  initialTemplates: PromoTemplateOption[];
};

const PromoTemplatesSelect = ({value, onChange, initialTemplates}: Props) => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [pickedTemplates, setPickedTemplates] = useState<PromoTemplateOption[]>([]);
  const {data, isFetching} = useGetTemplates(
    {paginationModel: {page: 0, pageSize: SEARCH_PAGE_SIZE}, filters: searchFilters(debouncedSearch)},
    {keepPreviousData: true}
  );

  const searchResults = useMemo<PromoTemplateOption[]>(
    () => (data?.items ?? []).map(({id, name, content_channel}) => ({id, name, content_channel})),
    [data]
  );
  const knownById = useMemo(
    () =>
      new Map([...initialTemplates, ...pickedTemplates, ...searchResults].map((template) => [template.id, template])),
    [initialTemplates, pickedTemplates, searchResults]
  );
  const selected = value.map((id) => knownById.get(id)).filter((t): t is PromoTemplateOption => Boolean(t));
  const unselectedResults = searchResults.filter((template) => !value.includes(template.id));

  const onTemplatesChange = (templates: PromoTemplateOption[]) => {
    setPickedTemplates(templates);
    onChange(templates);
  };

  return (
    <>
      <Autocomplete<PromoTemplateOption, true, false, false>
        multiple
        size="small"
        fullWidth
        filterSelectedOptions
        popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
        sx={autocompleteChevronSx}
        options={searchResults}
        value={selected}
        loading={isFetching}
        filterOptions={(options) => options}
        inputValue={search}
        onInputChange={(_, next, reason) => reason !== "reset" && setSearch(next)}
        onChange={(_, templates) => onTemplatesChange(templates)}
        getOptionLabel={templateLabel}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        noOptionsText="No templates found"
        renderTags={(templates, getTagProps) =>
          templates.map((template, index) => {
            const {key, onDelete} = getTagProps({index});
            return (
              <Box key={key} sx={{m: 0.25}}>
                <Chip size_="small" variant_="blue" label={templateLabel(template)} onDelete={onDelete} />
              </Box>
            );
          })
        }
        renderInput={(params) => <TextField {...params} placeholder="Search templates..." sx={INPUT_SX} />}
      />
      {debouncedSearch && unselectedResults.length > 1 && (
        <Box>
          <Button buttonType="text" size="small" onClick={() => onTemplatesChange([...selected, ...unselectedResults])}>
            Select all {unselectedResults.length} results
          </Button>
        </Box>
      )}
    </>
  );
};

export default PromoTemplatesSelect;
